import { useContext, useEffect, useMemo, useRef } from 'react';

import { ViewHealthContext } from './provider';
import type {
  UseViewHealthErrorMarkerOptions,
  UseViewHealthMarkerOptions,
  ViewHealthScopeOptions,
} from './types';

// The context has a no-op default (see provider.tsx), so consumers are safe
// to call this hook outside a mounted `ViewHealthProvider` — the returned
// value will quietly drop scope operations and marker fires. In production
// the App root always wraps everything in the provider, so the noop path is
// reserved for tests / storybook / isolated rendering contexts.
function useViewHealthContext() {
  return useContext(ViewHealthContext);
}

export function useViewHealthScope(
  scopeType: 'route' | 'overlay',
  scopeName: string,
  enabledOrOptions: boolean | ViewHealthScopeOptions = true
): void {
  const { startScope, endScope, updateScopeOptions } = useViewHealthContext();
  const scopeId = useMemo(
    () => `${scopeType}:${scopeName}`,
    [scopeType, scopeName]
  );
  const options = useMemo<ViewHealthScopeOptions>(
    () =>
      typeof enabledOrOptions === 'boolean'
        ? { enabled: enabledOrOptions }
        : enabledOrOptions,
    [enabledOrOptions]
  );
  const enabled = options.enabled ?? true;
  const { entityType, successVariant } = options;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    startScope(scopeId, scopeType, scopeName, { entityType, successVariant });
    return () => {
      endScope(scopeId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startScope, endScope, scopeId, scopeType, scopeName, enabled]);

  // Keep options live across re-renders so the beacon (which fires at
  // settlement, potentially long after mount) reflects the most recent
  // entity type / variant — e.g. the slideout learns the entity type
  // after its data fetch completes.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    updateScopeOptions(scopeId, { entityType, successVariant });
  }, [updateScopeOptions, scopeId, entityType, successVariant, enabled]);
}

export function useViewHealthMarker(
  markerName: string,
  options?: UseViewHealthMarkerOptions
): void {
  const ctx = useViewHealthContext();
  const ready = options?.ready ?? true;
  const effectiveScopeId =
    options?.scopeId ?? `route:${ctx.currentRoutePattern}`;
  // When the host component stays mounted across an in-page navigation (e.g.
  // `actions/*` wildcard: /actions → /actions/1 → /actions/2), the route
  // pattern string doesn't change but the underlying scope is cancelled and
  // recreated. `routeScopeVersion` bumps on every scope creation, so
  // depending on it here makes the effect re-run and re-fire the marker for
  // the new scope. Without this dep, the new scope sits empty until the 15s
  // timeout fires. Explicit scopeId overrides (overlays, nested scopes) opt
  // out — they're not tied to the route lifecycle.
  const routeScopeVersion =
    options?.scopeId == null ? ctx.routeScopeVersion : null;
  useEffect(() => {
    if (ready) {
      ctx.markReceived(effectiveScopeId, markerName);
    }
  }, [ctx, effectiveScopeId, markerName, ready, routeScopeVersion]);
}

/**
 * Fires a failure marker into the current route scope when `triggered`
 * transitions to `true`. The provider branches on whether the marker is
 * registered as a failure marker and emits an immediate `outcome: 'error'`
 * beacon with `error_type` set to the marker name.
 *
 * Rising-edge semantics: fires once when `triggered` becomes `true`;
 * subsequent renders with `triggered === true` are no-ops.
 */
export function useViewHealthErrorMarker(
  name: string,
  { triggered, scopeId }: UseViewHealthErrorMarkerOptions
): void {
  const ctx = useViewHealthContext();
  const effectiveScopeId = scopeId ?? `route:${ctx.currentRoutePattern}`;
  // True rising-edge: track previous triggered AND scopeId so we only fire
  // once per (scope, triggered=true) pair. When the scope changes (route
  // navigation) we reset the latch so the same marker can fire in the new
  // scope if triggered is still true on the NEXT render — but not on the
  // render that changed the scope.
  const prevRef = useRef<{ triggered: boolean; scopeId: string }>({
    triggered: false,
    scopeId: effectiveScopeId,
  });
  useEffect(() => {
    const prev = prevRef.current;
    const scopeChanged = prev.scopeId !== effectiveScopeId;
    if (scopeChanged) {
      // Skip the render that changed the scope — firing now would leak an
      // error from the old context into the new scope. Reset the latch to
      // `triggered: false` (not the current `triggered`) so the next render
      // in the new scope can fire a rising-edge `false → true` if the
      // failure condition still holds.
      prevRef.current = { triggered: false, scopeId: effectiveScopeId };
      return;
    }
    prevRef.current = { triggered, scopeId: effectiveScopeId };
    // Fire only on a rising edge within the same scope.
    if (triggered && !prev.triggered) {
      ctx.markReceived(effectiveScopeId, name);
    }
  }, [ctx, effectiveScopeId, name, triggered]);
}
